import {
    ActionReference,
    DefaultGameState,
    GameObjectReference,
    GameState,
    NavigationArrow,
    ReadingResult,
    ImageData,
    VideoData
} from "@shared/types";

import { GameEvent } from "../enums/GameEvent";
import { css, html, htmlArray } from "../helpers/webComponents";
import { SwitchRoomEvent, GameEventService, StopReadingEvent } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { PlayerSession } from "../../../api/src/game-implementation/types";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(45vh + 10px) minmax(calc(30vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        cursor: var(--cursor-default);
    }
    :host(.is-welding) {
        grid-template-rows: auto calc(15vh + 10px) minmax(calc(50vh + 10px), 1fr) auto;
    }

    :host:has(welding-game) .content {
        height: 100%;
    }

    .title {
        // position: relative;  /* Allows negative margins */
        // margin-bottom: -25px; /* Moves it downward, overlapping .header */
        // z-index: 2; /* Ensures it's above .header */

        place-self: center; /* Centers inside its grid cell */
        text-align: center;
        margin-top: 10px;
        overflow: auto;
        border-width: 0 35px; /* Border width */
        border-image: url("/assets/img/ui/title-border.png") 0 370 0 490 fill round;
        border-style: solid;
        max-width: max-content;
        padding: 8px 15px;
        display: flex;
    }

    .header {
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center; /* This centers the content horizontally */
        position: relative;
        margin-top: 10px;
        width: fit-content;
        margin-left: auto;
        margin-right: auto; /* Centers the header horizontally */
        border-width: 20px; /* Border width */
        border-image: url("/assets/img/ui/room-frame.png") 160 round;
        border-style: solid;
    }

    .header img {
        z-index: -1;
        width: calc(100% + 2vh); /* Adjusts dynamically based on screen width */
        height: calc(100% + 2vw);
        image-rendering: pixelated;
        user-select: none;
    }

    /* Apply styles only when the image alt is 'submarine' */
    .header img[alt="blueprint/submarine_complete.png"] {
        width: auto; /* Maintain image aspect ratio */
        height: 200px; /* Adjust the height of the image (larger than header height) */
        z-index: -1; /* Ensure the image is behind any other content, like game-readpanel */
        transform: translateY(15px);
    }

    /* Room image size, pls no touch without asking, otherwise it might break  */
    .header img:nth-child(1) {
        width: calc(100% + 2vh); /* Adjusts dynamically based on screen width */
        height: calc(100% + 2vw);
    }

    .header img:nth-child(n + 2) {
        position: absolute;
        width: auto; /* Let it be manually controlled */
        height: auto;
    }

    .header navigation-arrows {
        position: absolute; /* Position the arrows relative to the header */
        top: 0;
        left: 0;
        width: 100%; /* Match the width of the container (header's image) */
        height: 100%; /* Match the height to the image */
        pointer-events: none; /* Make clicks go through navigation-arrows element*/
    }

    game-pc,
    game-key,
    welding-game {
        z-index: 5;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content:has(game-pc) {
        display: flex;
        justify-content: center;
        align-items: center; /* If you also want vertical centering */
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .inventory-btn {
        position: absolute;
        margin-top: 40px;
        background-color: #856343;
        outline: 1px solid #5a3f25;
        border-top: 2px solid #c78d57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: var(--cursor-hover);
        display: inline-block;
        user-select: none;
        border-bottom: 2px solid #5a3f25;
        transition: all 0.1s ease-in-out;
        box-sizing: content-box;
    }

    .inventory-btn:hover {
        filter: brightness(1.1);
        transform: translateY(-2px);
        border-bottom-width: 4px;
        margin-bottom: 6px;
    }

    .inventory-btn:active,
    .inventory-btn:focus {
        cursor: var(--cursor-active);
        filter: brightness(0.9);
        transform: translateY(0px);
        border-bottom-width: 2px;
    }

    /* --------------- MENU STYLES */

    .menu {
        display: block;
        position: absolute;
        top: 10px;
        right: 20px;
        /* height: 64px; */
        z-index: 5;

        input[type="checkbox"],
        .hamburger-lines {
            display: block;
        }
        input[type="checkbox"]#menu-checkbox {
            position: absolute;
            display: block;
            height: 3rem;
            width: 3rem;
            right: 3%;
            top: 3%;
            z-index: 5;
            opacity: 0;
            pointer-events: none;
            cursor: var(--cursor-hover);
        }
        #menu-checkbox:active {
            cursor: var(--cursor-active);
        }

        .hamburger-lines {
            background-color: #856343;
            outline: 1px solid #5a3f25;
            border-top: 2px solid #c78d57;
            padding: 5px 10px;
            text-transform: uppercase;
            cursor: var(--cursor-hover);
            user-select: none;
            border-bottom: 2px solid #5a3f25;
            transition: all 0.1s ease-in-out;
            box-sizing: content-box;

            z-index: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 3rem; /* Adjust size */
            height: 2rem;
            position: relative;
            margin: 0;
        }
        .hamburger-lines:hover {
            filter: brightness(1.1);
            transform: translateY(-4px);
            border-bottom-width: 6px;
            margin-bottom: 6px;
        }
        .hamburger-lines:active,
        .hamburger-lines:focus {
            cursor: var(--cursor-active);
            filter: brightness(0.9);
            transform: translateY(0px);
            border-bottom-width: 2px;
        }

        .hamburger-lines .line {
            display: block;
            height: 4px;
            width: 100%;
            border-radius: 10px;
            background: #333;
            transform: translateX(10);
        }

        .hamburger-lines .line1 {
            transform-origin: 0% 0%;
            transition: transform 0.2s ease-in-out;
        }
        .hamburger-lines .line2 {
            transition: transform 0.2s ease-in-out;
        }
        .hamburger-lines .line3 {
            transform-origin: 0% 100%;
            transition: transform 0.2s ease-in-out;
        }

        .menu-items {
            position: absolute;
            top: 50px; /* Below the hamburger menu */
            right: 0;
            background: #000000a0;
            border-left: 1px solid #ffb546;
            padding: 10px;
            border-radius: 8px;
            box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.3);
            width: 150px; /* Set a fixed width */
            display: none; /* Hidden by default */
            flex-direction: column;
        }

        .menu-items li {
            list-style: none;
            margin-bottom: 1.5rem;
            font-size: 1.3rem;
            font-weight: 500;
            background-color: #8f54397a;
            padding: 10px;
            border-top: 1px solid #ad6433;
            border-bottom: 1px solid #35180f;
            cursor: var(--cursor-hover);
        }
        .menu-items li:hover {
            filter: brightness(1.1);
        }

        .menu-items a {
            border-radius: 3px;
            text-decoration: none;
            color: #ffd23e;
            font-weight: 500;
            /* transition: color 0.3s ease-in-out; */
        }

        /* Cool animation  for hamburger lines*/
        .menu.open .menu-items {
            transform: translateX(0);
        }
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #3a3738;
        background-image: url("/assets/img/ui/footer.png");
        border: 2px solid #be9e44;
        outline: 2px solid #ffd45d;
        border-width: 4px 0 0 0;
        margin-top: 10px;
        display: flex;
        height: 105px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
        width: 100%;
    }

    .footer .button {
        background-color: #856343;
        outline: 1px solid #5a3f25;
        border-top: 2px solid #c78d57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: var(--cursor-hover);
        display: inline-block;
        user-select: none;
        border-bottom: 2px solid #5a3f25;
        transition: all 0.1s ease-in-out;
        box-sizing: content-box;
    }

    .footer .button.active {
        pointer-events: none;
        filter: brightness(0.8);
        outline: 3px groove #d87b25;
    }

    .footer .button:hover {
        filter: brightness(1.1);
        transform: translateY(-4px);
        border-bottom-width: 6px;
        margin-bottom: 6px;
    }

    .footer .button:active,
    .footer .button:focus {
        cursor: var(--cursor-active);
        filter: brightness(0.9);
        transform: translateY(0px);
        border-bottom-width: 2px;
    }

    .footer .button.object-btn {
        background-color: #b17c4a;
        outline: 1px solid #5a3f25;
        border-top-color: #e49e5d;
        border-bottom-color: #794d24;
    }
    .game-over-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.7); /* Semi-transparent black */
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000; /* High z-index to ensure it's on top */
    }
    cut-scene {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: black;
    }

    /** For the Start-Up room. Please keep at the bottom of styles */
    :host:has(#start-up) {
        width: 100vw;
        max-width: none;
        height: 100vh;
        overflow: hidden;
        position: relative;
    }

    #start-up {
        /* Reset default layout */
        .header,
        .content,
        .footer,
        .footer .buttons,
        .footer .button {
            all: unset;
            box-sizing: border-box;
        }

        position: relative;
        width: 100vw;
        height: 100vh;

        .header {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            aspect-ratio: 16 / 9;
            z-index: 0;
            overflow: hidden;
        }

        .header img {
            max-width: 100%;
            max-height: 100%;
            aspect-ratio: 16 / 9;
            object-fit: contain; // keep aspect ratio, no stretching
            image-rendering: pixelated;
            user-select: none;
            pointer-events: none;
            overflow: hidden;
        }

        .content {
            position: absolute;
            bottom: 30px;
            left: 40vw;
            transform: translateX(-50%);
            z-index: 2;
            width: fit-content;
            background: rgba(0, 0, 0, 0.5); // optional background
            color: white;
            padding: 10px;
            text-align: center;
            border-radius: 10px;
            display: flex;
            flex-direction: row;
            /* flex-wrap: wrap;
            min-width: 28em; */

            p {
                margin: 10px;
            }
        }

        .footer {
            position: absolute;
            top: 30vw;
            right: 30px;
            transform: translateY(-50%);
            z-index: 2;
        }

        .footer .buttons div {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .footer .button {
            background-color: #856343;
            outline: 1px solid #5a3f25;
            border-top: 2px solid #c78d57;
            padding: 10px 15px;
            text-transform: uppercase;
            cursor: var(--cursor-hover);
            user-select: none;
            border-bottom: 2px solid #5a3f25;
            transition: all 0.1s ease-in-out;
            box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
            margin: 1px;

            &:hover {
                filter: brightness(1.1);
                transform: translateY(-4px);
                border-bottom-width: 6px;
                margin: 0;
            }

            &:active {
                cursor: var(--cursor-active);
                filter: brightness(0.9);
                transform: translateY(0px);
                border-bottom-width: 2px;
            }
        }
        
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _currentSession?: PlayerSession;
    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    private _mustBlockActions: boolean | undefined;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();

        setTimeout(() => this.inventoryButtonListener(), 0);
    }

    /**
     * Refresh the current game state
     */
    public async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        await this.updateGameState(state);

        // Set Event Listeners that react to the click on the navigation arrows
        this.listenToRoomNavigation();
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private async updateGameState(state: GameState): Promise<void> {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        this._currentSession = state.session;

        // this.clearCheckpoint();
        // await this.saveCheckpoint(state.roomAlias);

        // document.addEventListener("sound-complete", async (event: Event) => {
        //     const e: CustomEvent<AnnouncementRequest> = event as CustomEvent<AnnouncementRequest>;
        //     if (e.detail.sound === "airplane-crash.mp3") {
        //         await this.playCutscene(9);
        //     }
        // });

        if (this._currentGameState?.roomAlias === "airplane") {
            if (this._currentSession.crashAnnouncementPlayed) {
                await this.playCutscene(9);
            }
        }
        // console.log("canvascomponent cutscenenumber", cutsceneNumber);
        if (this._currentSession.cutsceneNumber > 0) {
            await this.playCutscene(this._currentSession.cutsceneNumber);
            return;
        }

        if (this._currentGameState?.roomAlias === "azarath" || this._currentGameState?.roomAlias === "startup") {
            if (this._currentSession.endGameState) {
                await this.playEndCredits();
                return;
            }
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        this.setRoomSound();

        // Block Menu and navigation arrows, block actions
        this.checkIfActionsAreBlocked();

        if (this._currentGameState.roomAlias === "startup") {
            await this.renderStartUp();
        }
        else {
            // Refresh the web component
            await this.render();
        }
    }

    // public clearCheckpoint(): void {
    //     localStorage.removeItem("lastCheckpoint");
    //     localStorage.removeItem("lastPlayerSession");
    // }

    // public async saveCheckpoint(roomAlias: string): Promise<void> {
    //     localStorage.setItem("lastCheckpoint", roomAlias);
    //     await this._gameRouteService.savePlayerSession();
    // }

    private checkIfActionsAreBlocked(): void {
        const gameState: DefaultGameState | undefined = this._currentGameState;

        if (gameState) {
            // It means that the airport audio is playing so, block stuff
            if (gameState.sounds.includes("airport-announcement.mp3") || gameState.sounds.includes("airplane-crash.mp3")) {
                this._mustBlockActions = true;
                return;
            }
            this._mustBlockActions = false;
        }
        this._mustBlockActions = false;
    }

    /**
     * Play a cutscene and wait for completion
     */
    private async playCutscene(cutsceneNumber: number): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        return new Promise(resolve => {
            const cutscene: HTMLElement = document.createElement("cut-scene");
            cutscene.setAttribute("cutscene-number", cutsceneNumber.toString());

            this.shadowRoot!.appendChild(cutscene);

            this._gameEventService.addGameEventListener(GameEvent.EndCutscene, async () => {
                cutscene.remove();
                await this._gameRouteService.crashAnnouncementPlayed();
                await this.refreshGameState();
                resolve();
            });
        });
    }

    private async playEndCredits(): Promise<void> {
        if (!this.shadowRoot) return;

        return new Promise<void>(resolve => {
            // Create and add the end credits component
            const credits: HTMLElement = document.createElement("end-credits");
            this.shadowRoot!.appendChild(credits);

            // Listen for when the credits finish scrolling
            this._gameEventService.addGameEventListener(GameEvent.EndCredits, () => {
                console.log("Thank you for playing the game! :)");
            });

            // Listen for when new game is requested
            this._gameEventService.addGameEventListener(GameEvent.NewGameRequest, async () => {
                // console.log("New game requested");

                // Reset the game state
                await this._gameRouteService.exitGame();

                // Refresh to show the initial game state
                await this.refreshGameState();

                // Resolve the promise to continue execution
                resolve();
            });
        });
    }

    private async renderStartUp(): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            <div id="start-up">
                ${this.createHeader()}
                ${this.renderContent()}
                ${await this.renderFooter()}
            </div>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        this.startTypewriterEffect();
    }

    /**
     * Render the contents of this page
     */
    private async render(): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            
            ${this.renderTitle()}
            ${this.createHeader()}
            ${this.renderContent()}
            ${await this.renderFooter()}
            
            ${!this._mustBlockActions ? this.renderInventory() : []}
            
            ${this.renderGameOver()}
            ${this.renderSeaBeastGame()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        this.startTypewriterEffect();

        if (this._currentGameState?.roomAlias !== "startup" && this.shadowRoot.querySelector("cut-scene") === null) {
            this.setMenuListeners();

            if (!this._mustBlockActions) {
                // Dispatch game event Update Navigation Arrows so that NavigationComponent receives the arrows
                this.callUpdateNavigationArrowsEvent();
                this.inventoryButtonListener();
            }
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the game over overlay if the game is over.
     *
     * @returns String with raw HTML for the game over overlay. Can be empty.
     */
    private renderGameOver(): string {
        if (this._currentSession!.deathMessage) {
            return "<game-over class='game-over-overlay'></game-over>";
        }

        return "";
    }

    /**
     * Render the inventory overlay if the inventory is opened.
     *
     * @returns String with raw HTML for the inventory overlay. Can be empty.
     */
    public renderInventory(): string {
        if (this._currentSession!.inventoryOpened) {
            return "<div class='game-over-overlay'><game-inventory></game-inventory></div>";
        }

        return "";
    }

    private renderSeaBeastGame(): string {
        if (this._currentSession!.isFightingSeaBeast) {
            return "<div class='content'><sea-beast-battle></sea-beast-battle></div>";
        }
        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private createHeader(): string {
        const roomImages: ImageData[] | undefined = this._currentGameState?.roomImages;
        const roomVideos: VideoData[] | undefined = this._currentGameState?.roomVideos;
        const arrows: NavigationArrow[] | undefined = this._currentGameState?.arrows;
        let headerHTML: string;

        if (roomImages && roomImages.length > 0) {
            if (this._currentGameState?.roomAlias !== "startup") {
                headerHTML = `
                <div class="header">
                ${roomImages.map((image, index) =>
                    `<img src="/assets/img/rooms/${image.url}"
                    ${index === 0 ? "" : `style="left: ${image.x}%; top: ${image.y}%; width: ${image.width}%"`} />`).join("")}
                    ${roomVideos && roomVideos.length > 0 ? roomVideos.map(video => `<video src="/assets/img/rooms/${video.url}" style="position: absolute; left: ${video.x}%; top: ${video.y}%; width: ${video.width}%" autoplay></video>`).join("") : ""}
                    ${arrows && arrows.length > 0 ? "<navigation-arrows></navigation-arrows>" : ""}
                    <game-readpanel></game-readpanel>
                </div>
                ${!this._mustBlockActions ? "<div class='inventory-btn' tabindex='0'>Inventory</div>" : ""}
                
                ${this.renderMenu()}
            `;
            }
            else {
                headerHTML = `
                    <div class="header">
                        ${roomImages.map(image => `<img src="/assets/img/rooms/${image.url}" />`).join("")}
                        ${arrows && arrows.length > 0 ? "<navigation-arrows></navigation-arrows>" : ""}
                    </div>
                `;
            }

            this.achievementNumber(this._currentSession!.achievementNumber);

            return headerHTML;
        }
        return "";
    }

    /**
     * Render the menu element.
     *
     * @returns String with raw HTML for the menu element.
     */
    private renderMenu(): string {
        const element: string = `
        <nav class="menu">
            <input type="checkbox" name="" id="menu-checkbox">
            <label for="menu-checkbox" class="menu-label">
                <div class="hamburger-lines">
                    <span class="line line1"></span>
                    <span class="line line2"></span>
                    <span class="line line3"></span>
                </div>
            </label>
            <ul class="menu-items">
                
                <li id="volume"><a>Volume</a></li>
            </ul>
        </nav>
        `;
        // <li id="new-game"><a>New Game</a></li>

        // the event listeners are applied through render in setMenuListeners()

        return element;
    }

    /**
     * Render the content element based on the current game state.
     *
     * @returns String with raw HTML for the content element.
     */
    private renderContent(): string {
        // Reset the style when not in a special room (e.g., blueprint-room)
        this.classList.remove("is-welding");
        if (this._currentGameState?.roomAlias === "home") {
            if (this._currentSession!.isPcUsed) {
                return "<div class='content'><game-pc></game-pc></div>";
            }
            else {
                return this.createContent();
            }
        }
        else if (this._currentGameState?.roomAlias === "old-metro") {
            if (this._currentSession!.isUsingKey) {
                return "<div class='content'><game-key></game-key></div>";
            }
            else {
                return this.createContent();
            }
        }
        else if (this._currentGameState?.roomAlias === "blueprint-room") {
            if (this._currentSession!.isWelding) {
                // fix the problem with the welding minigame size in order to be able to finish the game
                this.classList.add("is-welding");

                return "<div class='content'><welding-game></welding-game></div>";
            }
        }

        return this.createContent();
    }

    /**
 * Create the content element based on the current game state's text.
 *
 * @returns String with raw HTML for the content element.
 */
    private createContent(): string {
        if (!this._currentGameState?.text || this._currentGameState.text.length === 0) {
            return "<div class='content'></div>";
        }

        // Create the content div with empty paragraphs that will be animated
        const paragraphs: string = this._currentGameState.text.map(text =>
            `<p style="visibility: hidden;">${text}</p>`
        ).join("");

        return `
            <div class="content">
                ${paragraphs}
            </div>
        `;
    }

    /**
     * Start the typewriter effect after content is rendered
     */
    private startTypewriterEffect(): void {
        const contentDiv: HTMLDivElement | null | undefined = this.shadowRoot?.querySelector(".content");
        if (!contentDiv) return;

        const paragraphs: HTMLParagraphElement[] = Array.from(contentDiv.querySelectorAll("p"));
        let currentPara: number = 0;
        const speed: number = 30; // typing speed in ms
        const paraDelay: number = 500; // delay between paragraphs in ms

        const typeNext: () => void = () => {
            if (currentPara >= paragraphs.length) return;

            const p: HTMLParagraphElement = paragraphs[currentPara];
            const text: string = p.textContent || "";
            p.textContent = "";
            p.style.visibility = "visible";

            let charIndex: number = 0;
            const typeChar: () => void = () => {
                if (charIndex < text.length) {
                    p.textContent = (p.textContent || "") + text.charAt(charIndex);
                    charIndex++;
                    setTimeout(typeChar, speed);
                }
                else {
                    currentPara++;
                    setTimeout(typeNext, paraDelay);
                }
            };

            typeChar();
        };

        // Start the effect after a small delay to ensure DOM is ready
        setTimeout(typeNext, 100);
    }

    /**
     * Render the footer element with action and object buttons.
     *
     * @returns HTML element of the footer.
     */
    private async renderFooter(): Promise<HTMLElement> {
        const actionButtons: HTMLElement[] = this._currentGameState?.actions.map(button => this.renderActionButton(button)) || [];

        let objectButtons: HTMLElement[] = [];
        if (this._selectedActionButton) {
            objectButtons = await Promise.all(
                this._currentGameState?.objects.map(button => this.renderGameObjectButton(button, this._selectedActionButton as ActionReference)) || []
            );
        }

        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${actionButtons}
                    </div>
                    <div>
                        ${objectButtons}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference.
     *
     * @param button Action button reference.
     * @returns HTML element of the action button.
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${button.alias}-btn ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference.
     *
     * @param button Game object reference.
     * @param selectedAction Selected action reference.
     * @returns HTML element of the game object button.
     */
    private async renderGameObjectButton(button: GameObjectReference, selectedAction: ActionReference): Promise<HTMLElement> {
        const compatibleObjectAndAction: boolean = await this._gameRouteService.gameObjectForAction(button, selectedAction);
        // console.log("rendergameobjectbutton", compatibleObjectAndAction);

        if (!compatibleObjectAndAction) {
            return document.createElement("span"); // ✅ Always return an empty element instead of undefined
        }

        const element: HTMLElement = html`
            <a class="button object-btn ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        element.addEventListener("click", () => this.handleClickGameObject(button));
        // console.log("element: ", element);
        return element;
    }

    /**
     * Handle the click on an action button.
     *
     * @param button Action button that was clicked.
     */

    private _actionClickCount: number = 0;

    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this.playSound("button-click.mp3");
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.initializeAchievement2();

            await this.render();

            return;
        }

        this.playSound(button.alias, "action");

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        await this.updateGameState(state);
    }

    private initializeAchievement2(): void {
        this._actionClickCount++;

        if (this._actionClickCount === 15) {
            const alreadyUnlocked: boolean | undefined = this._currentSession?.achievementunlocked.find(a => a.case === 2)?.unlocked;
            if (!alreadyUnlocked) {
                this.achievementNumber(2);
            }
        }
    }

    /**
     * Handle the click on a game object button.
     *
     * @param button Game object button that was clicked.
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        let state: GameState | ReadingResult | undefined = undefined;
        // Check what action has been clicked, as Read action needs to get not the state, but the room images instead
        if (this._selectedActionButton.alias === "read") {
            state = await this._gameRouteService.getReadingPages(
                this._selectedActionButton.alias,
                [...this._selectedGameObjectButtons].map(e => e.alias)
            );
        }
        else {
            state = await this._gameRouteService.executeAction(
                this._selectedActionButton.alias,
                [...this._selectedGameObjectButtons].map(e => e.alias)
            );
        }
        // Try to execute the action with all game objects on the list

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        this.playSound(this._selectedActionButton?.alias ?? "action-btn", "action");

        // Refresh the web component
        await this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }
        else if ("images" in state) {
            this.sendImagesToReadComponent(state);
        }
        else {
            // Otherwise, update the game state.
            await this.updateGameState(state);
        }
    }

    /**
     * Dispatch game event Set Room Sounds so that SoundComponent receives the room sounds.
     */
    public setRoomSound(): void {
        const sounds: string[] | undefined = this._currentGameState?.sounds || undefined;

        if (sounds) {
            this._gameEventService.setRoomSound(sounds);
            // console.log("Sent the new room sounds");
        }

        if (this._currentSession?.survivorIsDead && !this._currentSession.survivorScreamPlayed) {
            setTimeout(() => {
                this.playSound("scream.mp3");
            }, 2000);
        }
    }

    /**
     * Play a sound based on the provided name and type.
     *
     * @param name Name/url of the sound file.
     * @param type Type of the sound (optional). Used to differentiate between action and other sounds.
     */
    private playSound(name: string, type?: string): void {
        // console.log(name, type);
        this._gameEventService.playSound(name, type);
    }

    /**
     * Dispatch game event Update Navigation Arrows so that NavigationComponent receives the arrows.
     */
    private callUpdateNavigationArrowsEvent(): void {
        const arrows: NavigationArrow[] | undefined = this._currentGameState?.arrows || undefined;

        if (arrows && arrows.length > 0) {
            this._gameEventService.updateNavigationArrows(arrows);
        }
    }

    /**
     * Set Event Listeners that react to the click on the navigation arrows and update the room.
     */
    private listenToRoomNavigation(): void {
        // Listen for the Switch Room event
        this._gameEventService.addGameEventListener<SwitchRoomEvent>(GameEvent.SwitchRoom, async event => {
            await this.updateGameState(event.state);
        });
    }

    /**
     * Dispatch game event Update Read Content so that ReadComponent receives the images after read action has been clicked.
     *
     * @param readingResult The result of the read action containing images.
     */
    public sendImagesToReadComponent(readingResult: ReadingResult): void {
        // const ReadComponent: HTMLElement = document.createElement("game-readpanel");
        // this.shadowRoot?.querySelector(".header")?.appendChild(ReadComponent);
        this._gameEventService.updateReadContent(readingResult);

        this.listenToStopReading();
    }

    /**
     * Set an Event Listener that reacts to the click on cross button from read action that would update the game state.
     */
    private listenToStopReading(): void {
        // Listen for the Switch Room event
        this._gameEventService.addGameEventListener<StopReadingEvent>(GameEvent.StopReading, async event => {
            await this.updateGameState(event.state);
        });
    }

    /**
     * Add event listeners to the inventory button for click and hover actions.
     */
    private inventoryButtonListener(): void {
        const inventoryButton: HTMLDivElement | null = this.shadowRoot!.querySelector(".inventory-btn");

        if (inventoryButton) {
            inventoryButton.addEventListener("click", () => {
                this.playSound("inventory-open.mp3");
                void this.handleOpenInventory();
            });
            inventoryButton.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        }
    }

    /**
     * Set event listeners for the menu, including toggling visibility and handling menu item clicks.
     */
    private setMenuListeners(): void {
        const menu: HTMLDivElement = this.shadowRoot!.querySelector(".menu") as HTMLDivElement;
        const menuCheckbox: HTMLInputElement = this.shadowRoot!.querySelector("#menu-checkbox") as HTMLInputElement;
        const menuButton: HTMLLabelElement = this.shadowRoot!.querySelector("#menu-checkbox") as HTMLLabelElement;
        const menuItems: HTMLDivElement = this.shadowRoot!.querySelector(".menu-items") as HTMLDivElement;

        // on press, display the menu
        menuCheckbox.addEventListener("change", () => {
            this.toggleMenu(menu, menuCheckbox, menuItems);
        });
        menuButton.addEventListener("click", () => this.playSound("button-click.mp3"));

        // hide the menu if clicked outside
        this.shadowRoot?.addEventListener("click", event => {
            if (!menu.contains(event.target as Node) && menuCheckbox.checked) {
                menuCheckbox.checked = false;
                this.toggleMenu(menu, menuCheckbox, menuItems);
            }
        });

        // open Volume Menu
        menu.querySelector("#volume")!.addEventListener("click", () => {
            this.playSound("button-click.mp3");
            this._gameEventService.SoundMenuOpen();
            menuCheckbox.checked = false;
            this.toggleMenu(menu, menuCheckbox, menuItems);
        });
    }

    /**
     * Toggle the visibility of the menu and apply animations to the hamburger lines.
     *
     * @param menu The menu container element.
     * @param menuCheckbox The checkbox controlling the menu state.
     * @param menuItems The menu items container element.
     */
    private toggleMenu(menu: HTMLDivElement, menuCheckbox: HTMLInputElement, menuItems: HTMLDivElement): void {
        if (menuCheckbox.checked) {
            menuItems.style.display = "flex";
            menu.classList.add("open");
            // animation
            (menu.querySelector(".line1") as HTMLSpanElement).style.transform = "translateX(10%) rotate(35deg)";
            (menu.querySelector(".line2") as HTMLSpanElement).style.transform = "translateX(10%) scaleY(0)";
            (menu.querySelector(".line3") as HTMLSpanElement).style.transform = "translateX(10%) rotate(-35deg)";
        }
        else {
            menuItems.style.display = "none";
            menu.classList.remove("open");
            (menu.querySelector(".line1") as HTMLSpanElement).style.transform = "rotate(0deg)";
            (menu.querySelector(".line2") as HTMLSpanElement).style.transform = "scaleY(1)";
            (menu.querySelector(".line3") as HTMLSpanElement).style.transform = "rotate(0deg)";
        }
    };

    /**
     * Handle the opening of the inventory by updating the game state.
     */
    private async handleOpenInventory(): Promise<void> {
        // console.log("Inventory button clicked!");
        await this._gameRouteService.openInventory();
        void this.refreshGameState();
    }

    private achievementNumber(number: number): void {
        if (number !== 0) {
            this._gameEventService.achievementNumber(number);
        }
    }
}
